import {
  forwardRef,
  NamedExoticComponent,
  ReactElement,
  ReactNode,
  Ref,
  RefAttributes,
} from 'react';
import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
} from '@mui/material';
import { SystemColors } from 'melp-design/style';

interface Props<Value> {
  items: Array<{ label: ReactNode; value: Value; disabled?: boolean }>;
  label?: string;
  direction?: 'row' | 'column';
  size?: 'md' | 'xl';
  name?: string;
  value?: Value;
  onChange?: (value: Value) => void;
  info?: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
}

const RadioInputComponent = <Value extends string | number>(
  {
    items,
    label,
    direction = 'column',
    size = 'md',
    name,
    value,
    onChange,
    info,
    error,
    required,
    disabled,
  }: Props<Value>,
  forwardedRef: Ref<HTMLInputElement>,
) => {
  return (
    <FormControl
      error={!!error}
      required={required}
      disabled={disabled}
      component="fieldset"
    >
      {label ? (
        <FormLabel
          component="legend"
          id={name}
          sx={{
            fontSize: 14,
            color: SystemColors.grey[45],
            '&.Mui-focused': { color: SystemColors.grey[45] },
          }}
        >
          {label}
        </FormLabel>
      ) : null}

      <RadioGroup
        aria-labelledby={name}
        name={name}
        value={value}
        onChange={({ target }) => onChange?.(target.value as Value)}
        row={direction === 'row'}
        sx={{ ...(direction === 'row' ? { gap: 1.25 } : {}) }}
        ref={forwardedRef}
      >
        {items.map((item) => (
          <FormControlLabel
            key={item.value}
            control={
              <Radio
                required={required}
                sx={{ padding: 1.75, pl: 1.25 }}
                disableRipple
              />
            }
            label={item.label}
            value={item.value}
            disabled={item.disabled}
            sx={{
              '& .MuiFormControlLabel-label': {
                fontSize: size === 'md' ? 14 : size === 'xl' ? 30 : undefined,
              },
            }}
          />
        ))}
      </RadioGroup>

      {error && <FormHelperText error>{error}</FormHelperText>}
      {info && <FormHelperText error={false}>{info}</FormHelperText>}
    </FormControl>
  );
};

export const RadioInput = forwardRef(RadioInputComponent) as <
  Value extends string | number,
>(
  p: Props<Value> & RefAttributes<HTMLInputElement>,
) => ReactElement;

(RadioInput as NamedExoticComponent).displayName = 'RadioInput';
